import { useState } from 'react';
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  type TooltipProps,
  Cell
} from 'recharts';
import { ChartContainer, type ChartConfig } from '../../../../components/ui/Chart';

// ----------------------------------------------------------------------

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-2, 173 58% 39%))',
  'hsl(var(--chart-3, 197 37% 24%))',
  'hsl(var(--chart-4, 43 74% 66%))',
  'hsl(var(--chart-5, 27 87% 67%))'
];

interface ChartRadialBarOccupancyProps {
  average: number;
  labelStrings: string[];
  series: number[];
  rawTotalValue?: number;
}

export default function ChartRadialBarOccupancy({
  average,
  labelStrings,
  series,
  rawTotalValue
}: ChartRadialBarOccupancyProps) {
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);

  const chartConfig: ChartConfig = {};
  labelStrings.forEach((name, i) => {
    chartConfig[name] = { label: name, color: CHART_COLORS[i % CHART_COLORS.length] };
  });
  chartConfig.average = { label: 'Average', color: 'hsl(var(--muted-foreground))' };

  const data = labelStrings.map((name, i) => ({
    name,
    value: Math.round(series[i] ?? 0),
    fill: CHART_COLORS[i % CHART_COLORS.length]
  }));

  const displayTotal = rawTotalValue ?? average;
  const centerValue = selectedSeries
    ? data.find((d) => d.name === selectedSeries)?.value ?? displayTotal
    : displayTotal;
  const centerLabel = selectedSeries ? `${centerValue}%` : `Avg ${displayTotal.toFixed(0)}%`;

  const handleSeriesSelect = (name: string) => {
    setSelectedSeries((prev) => (prev === name ? null : name));
  };

  const handleBarClick = (entry: { name?: string }) => {
    const name = entry?.name;
    if (name != null) handleSeriesSelect(name);
  };

  // Only show percent label inside the bar when it's selected (or when none selected)
  const renderBarLabel = (props: { value?: string | number; payload?: { name?: string } }) => {
    const name = props.payload?.name;
    const { value } = props;
    if (selectedSeries !== null && name !== selectedSeries) return null;
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return null;
    return `${num}%`;
  };
  /* eslint-enable react/prop-types */

  const renderTooltip: TooltipProps<number, string>['content'] = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const value = item.payload?.value ?? item.value ?? 0;
    const name = item.name ?? item.payload?.name ?? '';
    return (
      <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
        <div className="font-medium">{name}</div>
        <div>{`${Number(value).toFixed(1)}%`}</div>
      </div>
    );
  };

  const renderLegend = () => (
    <div className="flex flex-wrap justify-center gap-4 pt-2">
      {labelStrings.map((name, i) => {
        const isSelected = selectedSeries === null || selectedSeries === name;
        const color = CHART_COLORS[i % CHART_COLORS.length];
        return (
          <button
            key={name}
            type="button"
            className="flex items-center gap-1.5 text-sm transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-50"
            style={{ opacity: isSelected ? 1 : 0.5 }}
            onClick={() => handleSeriesSelect(name)}
            onKeyDown={(e) => e.key === 'Enter' && handleSeriesSelect(name)}
          >
            <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            <span>{name}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex h-[300px] w-full min-h-[300px] flex-col overflow-visible">
      <ChartContainer
        config={chartConfig}
        className="min-h-0 flex-1 [&_.recharts-responsive-container]:outline-none [&_.recharts-responsive-container]:focus:outline-none [&_.recharts-responsive-container]:ring-0 [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper]:focus:outline-none [&_.recharts-wrapper]:ring-0 [&_.recharts-radial-bar-sector]:outline-none [&_.recharts-radial-bar-sector]:focus:outline-none [&_.recharts-radial-bar-sector]:ring-0"
      >
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius="45%"
          outerRadius="80%"
          barSize={12}
          data={data}
          startAngle={0}
          endAngle={360}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
          <PolarRadiusAxis type="category" dataKey="name" tick={false} axisLine={false} />
          <RadialBar
            background
            dataKey="value"
            cornerRadius={4}
            onClick={handleBarClick}
            label={{ position: 'insideStart', content: renderBarLabel }}
          >
            {data.map((entry) => {
              const isSelected = selectedSeries === null || selectedSeries === entry.name;
              return <Cell key={entry.name} fill={entry.fill} opacity={isSelected ? 1 : 0.35} />;
            })}
          </RadialBar>
          <Tooltip content={renderTooltip} />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-foreground text-sm font-medium"
          >
            {centerLabel}
          </text>
        </RadialBarChart>
      </ChartContainer>
      <div className="shrink-0">{renderLegend()}</div>
    </div>
  );
}
